import { QBankStructureLevelCategory } from '@exam-management/graphql';
import { TableBody, type TableContainerProps } from '@shared/components';
import { useCallback, useState } from 'react';

import {
  ItemSetQuestionDashboardRow,
  LOSDashboardTableRow,
  DashboardTableRow,
  SummaryTableRow,
} from '../../components';
import { type QBankStructureDashboardData } from '../../types';

import { computeExpansionState } from './utils';

interface QBankStructureItemSetQuestionDashboardTableProps extends TableContainerProps {
  rows: QBankStructureDashboardData[];
  defaultLevel: QBankStructureLevelCategory;
}

const QBANK_STRUCTURE_LEVEL_CATEGORY_TO_INDENT = {
  [QBankStructureLevelCategory.Part]: 0,
  [QBankStructureLevelCategory.Subject]: 1,
  [QBankStructureLevelCategory.Group]: 2,
  [QBankStructureLevelCategory.Chapter]: 3,
  [QBankStructureLevelCategory.Topic]: 4,
  [QBankStructureLevelCategory.SubTopic]: 5,
  los: 4,
  'item-set-question': 5,
} as const;

// TODO: Rename this to QBankStructureDashboardTableBody
export default function QBankStructureItemSetQuestionTableBody(
  props: Readonly<QBankStructureItemSetQuestionDashboardTableProps>,
) {
  const { rows, defaultLevel } = props;
  const parentIdSet = new Set(
    rows.filter((row) => row.level === defaultLevel).flatMap((row) => row.parentIds ?? []),
  );
  const [openIds, setOpenIds] = useState<Set<string>>(parentIdSet);

  const toggleItem = useCallback((id: string) => {
    setOpenIds((prev) => {
      const updatedSet = new Set(prev);
      if (updatedSet.has(id)) {
        updatedSet.delete(id);
      } else {
        updatedSet.add(id);
      }
      return updatedSet;
    });
  }, []);

  const courseTableRow = rows.find((row) => row.level === QBankStructureLevelCategory.Course);

  return (
    <TableBody>
      {rows.map((row) => {
        const { id, parentIds, level } = row;

        const { isParentExpanded, isExpanded } = computeExpansionState(id, openIds, parentIds);

        if (!isParentExpanded) {
          return null;
        }

        switch (level) {
          case QBankStructureLevelCategory.Part:
          case QBankStructureLevelCategory.Subject:
          case QBankStructureLevelCategory.Group:
          case QBankStructureLevelCategory.Chapter:
          case QBankStructureLevelCategory.Topic:
          case QBankStructureLevelCategory.SubTopic: {
            const indent = QBANK_STRUCTURE_LEVEL_CATEGORY_TO_INDENT[level];

            return (
              <DashboardTableRow
                key={id}
                data={{
                  ...row,
                  level,
                  total: row.total && row.size ? `${row.size} (${row.total})` : undefined,
                }}
                expanded={isExpanded}
                onExpand={toggleItem}
                totalBoxes={indent}
              />
            );
          }
          case 'los': {
            const indent = QBANK_STRUCTURE_LEVEL_CATEGORY_TO_INDENT[level];

            return (
              <LOSDashboardTableRow
                key={id}
                data={{
                  ...row,
                  total:
                    row.total && row.size
                      ? `${row.size} (${row.total})`
                      : row.total
                        ? row.total.toString()
                        : undefined,
                }}
                expanded={isExpanded}
                onExpand={toggleItem}
                indent={indent}
              />
            );
          }
          case 'item-set-question': {
            const indent = QBANK_STRUCTURE_LEVEL_CATEGORY_TO_INDENT[level];

            return (
              <ItemSetQuestionDashboardRow
                key={id}
                question={{
                  size: row.size ?? 0,
                  calculate: {
                    hardQuestions: row.calculate?.hardQuestions ?? null,
                    mediumQuestions: row.calculate?.mediumQuestions ?? null,
                    easyQuestions: row.calculate?.easyQuestions ?? null,
                  },
                  nonCalculate: {
                    hardQuestions: row.nonCalculate?.hardQuestions ?? null,
                    mediumQuestions: row.nonCalculate?.mediumQuestions ?? null,
                    easyQuestions: row.nonCalculate?.easyQuestions ?? null,
                  },
                  total: row.total ?? 0,
                }}
                totalBoxes={indent}
              />
            );
          }
          default:
            return null;
        }
      })}
      {courseTableRow ? (
        <SummaryTableRow
          data={{
            ...courseTableRow,
            total:
              courseTableRow.size && courseTableRow.total
                ? `${courseTableRow.size} (${courseTableRow.total})`
                : courseTableRow.total
                  ? courseTableRow.total.toString()
                  : undefined,
          }}
        />
      ) : null}
    </TableBody>
  );
}
